import { differenceInDays, differenceInMonths, differenceInWeeks, endOfDay, endOfMonth, endOfWeek, set, startOfDay, startOfMonth, startOfWeek } from "date-fns";
import React from "react";
import { theme } from "src/config";

var parse = require('parse-color');


export const passwordValidation = (password: string): boolean => {
  const validPassword =
    /^((?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{6,}))|((?=.*[a-z])(?=.*[A-Z])(?=.*[^A-Za-z0-9])(?=.{8,}))$/;
  return validPassword.test(password) || password === "";
};

export const emailValidation = (email: string): boolean => {
  const validEmail =
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return validEmail.test(email) || email === "";
};

export const RGBtoHSV = (color: number[]): number[] => {
  const r   = color[0];
  const g   = color[1];
  const b   = color[2];
  const min = Math.min( r, g, b );
  const max = Math.max( r, g, b );

  const v = max;
  const delta = max - min;
  let s, h;
  if( max != 0 )
      s = delta / max;        // s
  else {
      // r = g = b = 0        // s = 0, v is undefined
      s = 0;
      h = -1;
      return [h, s, undefined];
  }
  if( r === max )
      h = ( g - b ) / delta;      // between yellow & magenta
  else if( g === max )
      h = 2 + ( b - r ) / delta;  // between cyan & yellow
  else
      h = 4 + ( r - g ) / delta;  // between magenta & cyan
  h *= 60;                // degrees
  if( h < 0 )
      h += 360;
  if ( isNaN(h) )
      h = 0;
  return [h,s,v];
};

export const HSVtoRGB = (color: number[]): number[] => {
  let   h = color[0];
  const s = color[1];
  const v = color[2];
  let r, g, b;

  if(s === 0 ) {
      // achromatic (grey)
      r = g = b = v;
      return [r,g,b];
  }
  h /= 60;            // sector 0 to 5
  const i = Math.floor( h );
  const f = h - i;          // factorial part of h
  const p = v * ( 1 - s );
  const q = v * ( 1 - s * f );
  const t = v * ( 1 - s * ( 1 - f ) );
  switch( i ) {
      case 0:
          r = v;
          g = t;
          b = p;
          break;
      case 1:
          r = q;
          g = v;
          b = p;
          break;
      case 2:
          r = p;
          g = v;
          b = t;
          break;
      case 3:
          r = p;
          g = q;
          b = v;
          break;
      case 4:
          r = t;
          g = p;
          b = v;
          break;
      default:        // case 5:
          r = v;
          g = p;
          b = q;
          break;
  }
  return [r,g,b];
}

export const hexToCSSRGBA = (color: string, alpha: number): string => {
  const rgb = hexToRGB(color);
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
}

/**
 * @return convert hex color string to [r, g, b] number array
 */
export const hexToRGB = (color: string): number[] => {
  if(!color) {
    console.error('hexToRGB', 'cannot convert color', color)
    return [0,0,0];
  }

  let m: any = color.match(/^#([0-9a-f]{3})$/i);
  if(m) {
      m = m[1];
      // in three-character format, each value is multiplied by 0x11 to give an
      // even scale from 0x00 to 0xff
      return [
          parseInt(m.charAt(0),16)*0x11,
          parseInt(m.charAt(1),16)*0x11,
          parseInt(m.charAt(2),16)*0x11
      ];
  }
  m = color.match(/^#([0-9a-f]{6})$/i);
  if(m) {
      m = m[1];
      return [
          parseInt(m.substr(0,2),16),
          parseInt(m.substr(2,2),16),
          parseInt(m.substr(4,2),16)
      ];
  }
  let mArr: RegExpMatchArray = color.match(/^rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);
  if(mArr) {
      return [parseInt(mArr[1]),parseInt(mArr[2]),parseInt(mArr[3])];
  }

  console.error("Could not parse color string", color);
  return undefined;
}

/**
 * @return convert [r,g,b] number array to hex color string
 */
export const RGBToHex = (color: number[]): string => {
  const r = Math.floor(color[0]).toString(16);
  const g = Math.floor(color[1]).toString(16);
  const b = Math.floor(color[2]).toString(16);
  return `#${r.length === 1 ? "0"+r : r}${g.length === 1 ? "0"+g : g}${b.length === 1 ? "0"+b : b}`;
}

/**
 * React HOC that allows to register a callback on a click outside of the component this
 * was called from. 
 * 
 * @param ref     React.useRef to container that defines "outside"
 * @param handler callback that is called on click
 */
export const useClickOutside = (ref: React.MutableRefObject<any>, handler: (arg0: any) => void) => {
  React.useEffect(() => {
    let startedInside = false;
    let startedWhenMounted = false;

    const listener = (event: MouseEvent) => {
      // Do nothing if `mousedown` or `touchstart` started inside ref element
      if (startedInside || !startedWhenMounted) return;
      // Do nothing if clicking ref's element or descendent elements
      if (!ref.current || ref.current.contains(event.target)) return;

      handler && handler(event);
    };

    const validateEventStart = (event: MouseEvent) => {
      startedWhenMounted = ref.current;
      startedInside = ref.current && ref.current.contains(event.target);
    };

    document.addEventListener("mousedown", validateEventStart);
    document.addEventListener("touchstart", validateEventStart);
    document.addEventListener("click", listener);

    return () => {
      document.removeEventListener("mousedown", validateEventStart);
      document.removeEventListener("touchstart", validateEventStart);
      document.removeEventListener("click", listener);
    };
  }, [ref, handler]);
};

export const HSLToRGB = (colorHSL: string): number[] => {
  const hsla = (parse(colorHSL))["hsl"];
  const h = hsla[0]; 
  const s = hsla[1]; 
  const l = hsla[2];
  
  let r, g, b;
  
  if(s === 0){
    r = g = b = l; // achromatic
  } else {
    let hue2rgb = function hue2rgb(p :number, q: number, t: number){
        if(t < 0) t += 1;
        if(t > 1) t -= 1;
        if(t < 1/6) return p + (q - p) * 6 * t;
        if(t < 1/2) return q;
        if(t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
    }

    let q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    let p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

export const getTextColorFromBackgroundColor = (cssColor: string): string => {
  const rgb = (parse(cssColor))["rgb"];
  const r = rgb[0];const g = rgb[1];const b = rgb[2];

  return getTextColorFromRGB(r, g, b);

}

/**
 * @colorHSL HSL color of the background
 * @return Font color, either theme.font.color.white or theme.font.color.primaryDark, based on backgorund color @color
 */
export const getTextColorFromHSL = (colorHSL: string): string => {
  const rgb = HSLToRGB(colorHSL);
  const r = rgb[0];const g = rgb[1];const b = rgb[2];

  return getTextColorFromRGB(r, g, b);
}

/**
 * @colorHex hex color of the background
 * @return Font color, either theme.font.color.white or theme.font.color.primaryDark, based on backgorund color @color
 */
export const getTextColorFromHex = (colorHex: string): string => {
  const rgb = hexToRGB(colorHex);
  const r = rgb[0];const g = rgb[1];const b = rgb[2];

  return getTextColorFromRGB(r, g, b);
}
/**
 * @color [r, g, b] values in 0..255 of the background
 * @return Font color, either theme.font.color.white or theme.font.color.primaryDark, based on backgorund color @color
 */
export const getTextColorFromRGB = (r: number, g: number, b: number): string => {
  // Calculate green adjusted perceived luminance of the background
  const luminance = ( 0.299 * r + 0.587 * g + 0.114 * b)/255;

  return luminance > 0.5 ? theme.font.color.black : theme.font.color.white;
}

/**
 * Returns the value of the css prop @prop of @el
 */
export const getCssStyle = (el: HTMLElement, prop: string): string => {
  return window.getComputedStyle(el, null).getPropertyValue(prop);
}
/**
 * Returns the font string including weight, size and family of @el
 */
export const getElementFontStyle = (el: HTMLElement = document.body): string => {
  const fontWeight = getCssStyle(el, 'font-weight') || 'normal';
  const fontSize   = getCssStyle(el, 'font-size')   || '16px';
  const fontFamily = getCssStyle(el, 'font-family') || 'Times New Roman';
  
  return `${fontWeight} ${fontSize} ${fontFamily}`;
}

/**
  * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
  * 
  * @param {String} text The text to be rendered.
  * @param {String} font The css font descriptor that text is to be rendered with (e.g. "bold 14px verdana").
  * 
  * @see https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
  */
export const getTextWidth = (() => {
  const canvas: HTMLCanvasElement = document.createElement("canvas");
  return (text: string, font: string): number => {
    const context = canvas.getContext("2d");
    context.font = font;
    const metrics = context.measureText(text);
    return metrics.width;
  }
})()

export const getCaretPos = (): DOMRect => {
  const selection: Selection = document.getSelection();
  
  if(selection.rangeCount === 0) return null;

  return selection.getRangeAt(0).getBoundingClientRect();
}

/**
 * Debounces a callback function with a given delay. Usage within a funcitonal component:
 * 
 * const clbk = (arg: any) => { ... };
 * const debouncedClbk = React.useCallback(debounce(clbk), []);
 * React.useEffect(() => debouncedChange(arg), [someState]);
 * 
 * @param clbk          (args: any) => void | Callback function to be delayed
 * @param debounceDelay number              | Delay in ms. Default is 500ms
 */
export const debounce = (clbk: (args?: any) => void, debounceDelay: number = 500) => { 
  let debounceTimer: NodeJS.Timeout = null;
  return (args?: any) => {
    clearTimeout(debounceTimer); 
    debounceTimer = setTimeout(() => clbk(args), debounceDelay); 
  }
}

/**
 * @return true if @a and @b have the same values. Objects are compared deeply.
 */
export const deepCmp = (a: any, b: any): boolean => {
  // If a and b point towards the same object, return true.
  if(a === b) return true;

  if(typeof a === "object" && a && b) {
    for(const key in a) if(!deepCmp(a[key], b[key])) return false;
    for(const key in b) if(!(key in a)) return false;
    return true;
  } else 
    return a === b;
}

/**
 * Takes a values and converts it to a number. Returns undefined if not possible.
 * 
 * @param x 
 * @return Number(x), unless it is NaN or x is null or undefined.
 */
export const toNumberOrUndefined = (x: number | string | null | undefined): number | undefined => {
  if (x == null) return undefined;

  const n = Number(x);

  return Number.isNaN(n) ? undefined : n;
}


export const without = <T>(arr1: T[], arr2: T[]): T[] => {
  return arr1.filter(a => !arr2.includes(a));
}

/**
 * Adds a value to an array if the array does not include it, removes it otherwise.
 */
export const toggleArrayValue = <T>(arr: T[], val: T, cmp?: (lhs: T, rhs: T) => boolean) => {
  if(!arr) return [val];
  else if(cmp ? arr.find(o => cmp(val, o)) : arr.includes(val)) return arr.filter(o => cmp ? !cmp(o, val): o !== val);
  else return arr.concat([val]);
}

/**
 * Get difference between two dates based on the provided interval
 * E.g. with @d1 in november and @d2 in december and @interval set to "months"
 * this will return 1.
 * 
 * @param d1 Date
 * @param d2 Date
 * @param interval Either days, weeks or months
 * 
 * @return Difference in @interval between @d1 and @d2
 */
export const getDateDiff = (d1: Date, d2: Date, interval: "days" | "weeks" | "months") => {
  switch (interval) {
    case "days":  return differenceInDays  (d1, d2);
    case "weeks": return differenceInWeeks (d1, d2);
    default:      return differenceInMonths(d1, d2);
}};

/**
 * Returns start and end of the @interval around @d 
 * E.g. with @interval set to months and @d set to the 2nd of January this would return
 * [Date obj of start of 1st of Jan, Date obj of end of 31st of Jan]
 * 
 * @param d Date
 * @param interval Either days, weeks or months
 * 
 * @return an array with two Date objects inside, the first is the start of the @interval of @d ,
 *          the second the end. 
 */
export const rangeOfDateInterval = (d: Date, interval: "days" | "weeks" | "months") => {
  switch (interval) {
    case "days":  return [startOfDay(d), endOfDay(d)];
    case "weeks": return [startOfWeek(d, {weekStartsOn: 1}), endOfWeek(d, {weekStartsOn: 1})];
    default:      return [startOfMonth(d), endOfMonth(d)];
}};

export const startOf = (d: Date, interval: "days" | "weeks" | "months"): Date => {
  return interval === "days" ? startOfDay(d) : (interval === "weeks" ? startOfWeek(d, {weekStartsOn: 1}) : startOfMonth(d));
}

export const endOf = (d: Date, interval: "days" | "weeks" | "months"): Date => {
  return interval === "days" ? endOfDay(d) : (interval === "weeks" ? endOfWeek(d, {weekStartsOn: 1}) : endOfMonth(d));
}

/**
 * Reset the time component of a date to 0
 */
export const resetTime = (date: Date) => {
  return set(date, {hours: 0, minutes: 0, seconds: 0, milliseconds: 0});
}

/**
 * Strip string from any html tags
 */
export const strip = (text: string): string => {
  return text.replace(/<[^>]+>/g, '');
}

export const intersect = <T>(lhs: T[], rhs: T[]): T[] => {
  return lhs.filter(i => rhs.includes(i));
}

export const sortAlph = <T>(list: T[], map: (obj: T) => string | number, desc?: boolean): T[] => {
  return list.sort((lhs: T, rhs: T) => 
    desc ? (
      map(lhs) > map(rhs) ? -1 : (map(lhs) < map(rhs) ? 1 : 0)
    ) : (
      map(lhs) < map(rhs) ? -1 : (map(lhs) > map(rhs) ? 1 : 0)
    )
  );
}